<template>
    <div class="flex flex_column">
        <el-tabs class="flex_1 margin_bottom" tab-position="left"
                 :value="canEditUserRoles ? actionTabs.userRoles.name : actionTabs.seller.name"
        >
            <el-tab-pane v-for="tab in visibleActionTabs"
                         :name="tab.name" :key="tab.name"
            >
                <span slot="label" class="margin_r">
                    <i class="tab_icon fa" :class="tab.icon" :style="tab.iconStyle"></i>
                    {{$t(tab.label)}}
                </span>
                <div class="tab_content margin_top" v-if="permissions">
                    <template v-if="tab.type === tabTypes.userRole">
                        <UserRole v-for="role of tab.roles"
                                  :key="role.id"
                                  :user-role="role"
                                  :allow-change="allowUserRoleChange"
                                  :can-edit="canEditUserRoles"
                                  :granted="isRoleGrantedToUser(role)"
                                  @change="loadPermissions"
                                  ref="userRole"
                                  class="margin_bottom"
                        ></UserRole>
                    </template>
                    <template v-if="tab.type === tabTypes.action">
                        <div v-if="!hasActionsForTab(tab)" class="empty">
                            {{ $t("manageUser.permissions.emptyCategory", { category: $t(tab.label) }) }}
                        </div>
                        <Permission v-else
                                    v-for="action of getActionsForTab(tab)"
                                    :key="action.id"
                                    :action="action"
									:is-from-company="isActionPartOfTheUsersCompany(action)"
                                    :is-from-role="isActionPartOfTheUsersRole(action)"
                                    :can-edit="canEditPermissions"
                                    :granted="isActionGrantedToUser(action)"
									@permission-changed="handlePermissionChanged"
                                    ref="action"
                                    class="margin_bottom"
                        >
                            <SellLimits v-if="action.resources === enumDef.permissions.SellerSellReceivables"
                                        :accounts="permissions.settlementAccounts"
                                        :limits="permissions.limits"
                                        :can-edit-limits="canEditLimits"
                                        ref="sellLimits"
                            />
                            <BuyLimits v-if="action.resources === enumDef.permissions.InvestorExchangeReceivables"
                                       :user-data="userData"
                                       :accounts="permissions.buyAccounts"
                                       :assigned-accounts="permissions.assignedAccounts"
                                       :limits="permissions.limits"
                                       :can-edit-limits="canEditLimits"
                                       :can-edit-accounts="canEditAccounts"
									   :is-deputy-granted="isDeputyGranted"
                                       ref="buyLimits"
                            />
                        </Permission>
                    </template>
                </div>
            </el-tab-pane>
        </el-tabs>
        <div class="flex flex_end w_100 margin_top">
            <md-button class="md-blank margin_r" @click="close">
                {{$t("common.button.close")}}
            </md-button>
            <md-button class="md-primary" @click="submit">
                {{$t("common.button.submit")}}
            </md-button>
        </div>
    </div>
</template>

<script>
import Permission from "@/components/templates/modal/users/Permission";
import {enumDef} from "@/common/enumDefs";
import {traderTeamManagementApi} from "@/common/api/traderTeamManagementApi";
import SellLimits from "@/components/templates/modal/users/SellLimits";
import BuyLimits from "@/components/templates/modal/users/BuyLimits";
import UserRole from "@/components/templates/modal/users/UserRole";
import {hasRequiredTraderType} from "@/common/permission/permission";

export default {
    props: {
        userData: { type: Object, default: null },
    },
    components: { UserRole, BuyLimits, SellLimits, Permission },
    data() {
        let tabTypes = {
            userRole: "userRole",
            action: "action"
        };
        let actionTabs = {
            userRoles: {
                type: tabTypes.userRole,
                icon: "fa-user",
                iconStyle: "padding-left: 1px",
                label: "manageUser.permissions.userRoles",
	            onlyVisibleFor: [enumDef.traderType.Supplier, enumDef.traderType.Investor],
				roles: [
					{
						id: enumDef.userRole.TraderAdmin,
						title: "manageUser.userRole.traderAdmin.title",
						description: "manageUser.userRole.traderAdmin.description"
					},
					// Currently we have no other trader roles that can be changed
				],
                shouldHide: () => !this.allowUserRoleChange
            },
            seller: {
                type: tabTypes.action,
				actions: [
					// Do not show until feature is redesigned.
					/*enumDef.permissions.SellerInvestorManagement,*/
					enumDef.permissions.SellerDebtorManagement,
					enumDef.permissions.SellerSellReceivables,
					// https://walbing-jira.atlassian.net/browse/DEV-5659
					// enumDef.permissions.SellerRevolvingPutOptions,
					enumDef.permissions.SellerManualPayment,
					enumDef.permissions.SellerTrackAndPay
				],
                icon: "fa-upload",
                label: "manageUser.permissions.seller",
	            onlyVisibleFor: [enumDef.traderType.Supplier, enumDef.traderType.Investor /* Remove Investor from here later */],
            },
            investor: {
                type: tabTypes.action,
				actions: [
					// https://walbing-jira.atlassian.net/browse/DEV-5659
					// enumDef.permissions.InvestorExchangeRevolvingPutOptions,
					enumDef.permissions.InvestorExchangeReceivables,
					enumDef.permissions.InvestorExchangeAlfa,
					enumDef.permissions.InvestorCustomRatings
				],
                icon: "fa-pie-chart",
                label: "manageUser.permissions.investor",
	            onlyVisibleFor: [enumDef.traderType.Investor],
            },
            debtor: {
                type: tabTypes.action,
				actions: [
					enumDef.permissions.DebtorIpuConfirmations,
					enumDef.permissions.DebtorTrackAndPay
				],
                icon: "fa-building",
                label: "manageUser.permissions.debtor",
	            onlyVisibleFor: [enumDef.traderType.Supplier, enumDef.traderType.Investor],
            },
            administrative: {
                type: tabTypes.action,
				actions: [
					enumDef.permissions.AdminProfile,
					enumDef.permissions.AdminUserManagement,
					enumDef.permissions.AdminCompany,
					enumDef.permissions.AdminBankAccountManagement,
					enumDef.permissions.AdminInvoiceManagement
				],
                icon: "fa-cog",
                label: "manageUser.permissions.admin",
	            onlyVisibleFor: [enumDef.traderType.Supplier, enumDef.traderType.Investor],
            },
        };
        return {
            tabTypes: tabTypes,
            actionTabs: actionTabs,
            permissions: null,
			enumDef: enumDef,
			isDeputyGrantedToUser : false
        };
    },
    computed: {
		isDeputyGranted() {
			return this.isDeputyGrantedToUser
		},
        visibleActionTabs() {
            let visibleTabs = {};
            for (let tabKey in this.actionTabs) {
                let tab = this.actionTabs[tabKey];
                if (hasRequiredTraderType(this.currentUser, tab.onlyVisibleFor) && (!tab.shouldHide || !tab.shouldHide())) {
                    visibleTabs[tabKey] = tab;
					visibleTabs[tabKey].name = tabKey;
                }
            }
            return visibleTabs;
        },
        isEditMode() {
            return this.userData && this.userData.id;
        },
        allowUserRoleChange() {
            return !this.isEditMode
                || this.userData.userLevel !== enumDef.userLevel.RootUser;
        },
        canEditUserRoles() {
            return this.currentUser.level === enumDef.userLevel.RootUser
                && (!this.userData || this.currentUser.userId !== this.userData.id);
        },
        canEditPermissions() {
            if (!this.isEditMode) {
                return [enumDef.userLevel.RootUser, enumDef.userLevel.ManagerUser].includes(this.currentUser.level);
            } else {
                return this.currentUser.level < this.userData.userLevel;
            }
        },
        canEditLimits() {
            if (!this.isEditMode) {
                return [enumDef.userLevel.RootUser, enumDef.userLevel.ManagerUser].includes(this.currentUser.level);
            } else {
                return (this.currentUser.userId === this.userData.id && [enumDef.userLevel.RootUser, enumDef.userLevel.ManagerUser].includes(this.currentUser.level))
                    || this.currentUser.level < this.userData.userLevel
            }
        },
        canEditAccounts() {
            if (!this.isEditMode) {
                return [enumDef.userLevel.RootUser, enumDef.userLevel.ManagerUser].includes(this.currentUser.level);
            } else {
                return (this.currentUser.userId === this.userData.id && [enumDef.userLevel.ManagerUser].includes(this.currentUser.level))
                    || this.currentUser.level < this.userData.userLevel
            }
        },
    },
    mounted() {
        this.loadPermissions();
    },
    methods: {
        loadPermissions(role, value) {
            let api = this.isEditMode
                ? traderTeamManagementApi.inspection.getUserPermissions(this.userData.id, this.getGrantedUserRoles())
                : traderTeamManagementApi.inspection.getPermissions(this.getGrantedUserRoles());

            api.then(res => {
                    this.permissions = res;

                    // only show enabled accounts ordered by main and id
                    this.permissions.buyAccounts = this.permissions.buyAccounts.filter(account => account.enabled);
                    this.permissions.settlementAccounts = this.permissions.settlementAccounts.filter(account => account.enabled);

                    let mainFirstThenById = (a1, a2) => {
                        if (a1.main) {
                            return -1;
                        } else if (a2.main) {
                            return 1;
                        } else {
                            return a1.id - a2.id
                        }
                    };
                    this.permissions.buyAccounts.sort(mainFirstThenById);
                    this.permissions.settlementAccounts.sort(mainFirstThenById);
                }).catch(e => {
                    this.toast(e);
                });


	        if (role && role.id == 3) {
		        this.isDeputyGrantedToUser = value;
	        }
        },
        close() {
            this.$emit("close");
        },
        submit() {
            this.$emit("submit");
        },
        hasActionsForTab(tab) {
            return this.permissions.ownActions.some(action => tab.actions.includes(action.resources)) !== undefined;
        },
        getActionsForTab(tab) {
            let tabActions = this.permissions.ownActions.filter(action => tab.actions.includes(action.resources));
			tabActions.sort((a1, a2) => tab.actions.indexOf(a1.resources) - tab.actions.indexOf(a2.resources));

            return tabActions;
        },
		getUserAction(action) {
			return this.permissions.userActions.find(userAction => userAction.id === action.id);
		},
        isActionGrantedToUser(action) {
            if (!this.permissions.userActions) {
                return false;
            }

            return this.getUserAction(action) !== undefined;
        },
        isActionPartOfTheUsersRole(action) {
            if (!this.permissions.userActions) {
                return false;
            }

            let userAction = this.getUserAction(action);
            return userAction ? userAction.src === "role" : false;
        },
		isActionPartOfTheUsersCompany(action) {
			if (!this.permissions.userActions) {
				return false;
			}

			let userAction = this.getUserAction(action);
			return userAction ? userAction.src === "company" : false;
		},
        isRoleGrantedToUser(role) {
            if (!this.permissions.userRoles) {
                return false;
            }

            return this.permissions.userRoles.find(userRoleId => userRoleId === role.id) !== undefined;
        },
        getGrantedUserRoles() {
            if (!this.permissions) {
                return null;
            }

            if (!this.$refs.userRole) {
                return this.permissions.userRoles;
            }

            return this.$refs.userRole.filter(ref => ref.granted).map(ref => ref.id);
        },
        getGrantedPermissions() {
            if (!this.canEditPermissions || !this.$refs.action) {
                return null;
            }

            return this.$refs.action.filter(ref => ref.granted);
        },
		getGrantedPermissionIds() {
			return this.getGrantedPermissions().map(ref => ref.id);
		},
        getLimits() {
            if (!this.canEditLimits) {
                return null;
            }

            let sellLimits = this.$refs.sellLimits ? this.$refs.sellLimits[0].getLimits() : [];
            let buyLimits = this.$refs.buyLimits ? this.$refs.buyLimits[0].getLimits() : [];

            let mergedLimits = [];
            sellLimits.forEach(limit => {
                mergedLimits.push(limit);
            });
            buyLimits.forEach(limit => {
                let existingLimitIndex = mergedLimits.findIndex(value => value.accountId === limit.accountId);
                if (existingLimitIndex >= 0) {
                    mergedLimits[existingLimitIndex] = {
                        ...mergedLimits[existingLimitIndex],
                        ...limit
                    };
                } else {
                    mergedLimits.push(limit);
                }
            });

            return mergedLimits;
        },
        getAssignedAccounts() {
            if (!this.canEditAccounts || !this.$refs.buyLimits) {
                return null;
            }

            return this.$refs.buyLimits[0].getAssignedAccounts();
        },
		handlePermissionChanged(action, granted) {
			if (action.resources === this.enumDef.permissions.SellerSellReceivables) {
				let permissionForDebtorManagement = this.$refs.action.find(ref => ref.action?.resources === this.enumDef.permissions.SellerDebtorManagement);
				if (!permissionForDebtorManagement) {
					return;
				}

				if (granted) {
					permissionForDebtorManagement.granted = true;
					permissionForDebtorManagement.canEdit = false;
					permissionForDebtorManagement.lockedByOtherPermission = true;
				} else {
					permissionForDebtorManagement.canEdit = true;
					permissionForDebtorManagement.lockedByOtherPermission = false;
				}
			}
		}
    },
    watch: {
        userData() {
            this.loadPermissions();
        },
    }
};
</script>

<style lang="scss" scoped>
.tab_content {
	min-height: 450px;
    max-height: calc(100vh - 450px);
    overflow-y: auto;
    overflow-x: hidden;

    .empty {
        position: relative;
        top: 20%;
    }
}

.el-tabs--left::v-deep {
    .el-tabs__item {
        padding: 0 10px 0 0;
    }
    .el-tabs__item.is-left {
        text-align: left;
    }
}

</style>
